import { useCallback, useEffect, useState } from 'react';
import eventsApi from 'api/requests/events';
import serviceFee, { PLATFORM_DEFAULT_FEE } from 'helpers/serviceFee';
import { useEvent } from 'context/events/Event';

/**
 * Cache por slug, no nível do módulo.
 *
 * A tela de ingressos monta um formulário por ingresso, e cada um tem o seu
 * formulário de lotes dentro — todos perguntando a mesma taxa do mesmo evento.
 * Sem isto seriam duas requisições por ingresso para uma resposta que é a
 * mesma. `inFlight` cobre o caso que o cache sozinho não pega: os componentes
 * montam juntos, antes da primeira resposta voltar, então todos compartilham
 * a mesma promessa em vez de disparar cada um a sua.
 *
 * Falha não entra no cache: a próxima montagem tenta de novo.
 */
const cache = new Map();
const inFlight = new Map();

function loadFee(slug) {
    if (cache.has(slug)) {
        return Promise.resolve(cache.get(slug));
    }

    if (inFlight.has(slug)) {
        return inFlight.get(slug);
    }

    const request = eventsApi.getFees(slug)
        .then(({ data }) => {
            if (!data?.data) {
                return null;
            }

            const fee = {
                rate: Number(data.data.credit_card_fee_rate) || 0,
                minimum: Number(data.data.minimum_per_unit) || 0,
            };

            cache.set(slug, fee);

            return fee;
        })
        .catch(() => null)
        .finally(() => inFlight.delete(slug));

    inFlight.set(slug, request);

    return request;
}

/**
 * Esquece a taxa de um evento. Quem altera as taxas precisa chamar isto, senão
 * a tela de ingressos segue mostrando a taxa antiga até recarregar a página.
 */
export function forgetEventServiceFee(slug) {
    cache.delete(slug);
}

/**
 * A taxa de serviço do evento aberto, com a conta pronta para usar.
 *
 * A taxa deixou de ser fixa em 10%: cada evento pode ter a sua em
 * `event_fee_settings`, e quem responde é `/events/detail/{slug}/fees`. Este
 * hook existe para que nenhuma tela precise saber disso — quem recopiava a
 * fórmula acabava recopiando o 10% junto, que foi como o formulário de lotes
 * ficou mostrando a taxa padrão mesmo em evento negociado.
 *
 * `ready` é a parte que importa: enquanto a resposta não chega, ou se ela
 * falha, a tela deve mostrar "—" em vez de um número. Uma taxa errada com cara
 * de certa é pior que taxa nenhuma, porque o produtor precifica em cima dela.
 */
export default function useEventServiceFee() {
    const { event } = useEvent();
    const slug = event?.slug;

    // Com o cache quente o primeiro render já sai com o valor: um ingresso
    // aberto depois não pisca "—" para reexibir o número que já está na tela
    // do ingresso ao lado.
    const [fee, setFee] = useState(() => (slug ? cache.get(slug) ?? null : PLATFORM_DEFAULT_FEE));

    useEffect(() => {
        // Evento que ainda não existe não tem taxa negociada: valem os padrões
        // da plataforma, e não há requisição a fazer.
        if (!slug) {
            setFee(PLATFORM_DEFAULT_FEE);
            return undefined;
        }

        const cached = cache.get(slug);

        if (cached) {
            setFee(cached);
            return undefined;
        }

        let isMounted = true;

        setFee(null);

        loadFee(slug).then((value) => isMounted && setFee(value));

        return () => { isMounted = false; };
    }, [slug]);

    const feeRate = fee?.rate ?? 0;
    const feeMinimum = fee?.minimum ?? 0;

    const calculateFee = useCallback(
        (price) => serviceFee.forPrice(price, feeRate, feeMinimum),
        [feeRate, feeMinimum]
    );

    const calculatePriceFromNet = useCallback(
        (desiredNet) => serviceFee.priceFromNet(desiredNet, feeRate, feeMinimum),
        [feeRate, feeMinimum]
    );

    return { feeRate, feeMinimum, ready: fee !== null, calculateFee, calculatePriceFromNet };
}
